import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';
import {
  Bot, KeyRound, Copy, RefreshCw, Trash2, Terminal, AppWindow, ShieldCheck, Plug, Unplug
} from 'lucide-react';
import api from '../services/api';

// The MCP endpoint lives next to the REST API, so derive it from the same
// base URL the app itself talks to. baseURL can be relative ("/api" in
// local dev via CRA proxy) — snippets go into external MCP clients, so
// resolve it against the page origin to always emit an absolute URL.
const rawApiBase = (api.defaults.baseURL || '/api').replace(/\/+$/, '');
const MCP_ENDPOINT = /^https?:\/\//i.test(rawApiBase)
  ? `${rawApiBase}/mcp`
  : `${window.location.origin}${rawApiBase.startsWith('/') ? '' : '/'}${rawApiBase}/mcp`;

const KEY_PLACEHOLDER = 'crmmcp_YOUR_KEY_HERE';

const CodeBlock = ({ text, onCopy }) => (
  <div className="relative group">
    <pre className="bg-gray-900 text-gray-100 text-xs rounded-lg p-3 pr-10 overflow-x-auto whitespace-pre-wrap break-all">
      {text}
    </pre>
    <button
      onClick={() => onCopy(text)}
      title="Copy"
      className="absolute top-2 right-2 p-1.5 rounded text-gray-400 hover:text-white hover:bg-gray-700"
    >
      <Copy size={14} />
    </button>
  </div>
);

const ClaudeSettings = () => {
  const queryClient = useQueryClient();
  // Plaintext key exists only in the generate response — held in local
  // state for one-time display, never persisted client-side.
  const [freshKey, setFreshKey] = useState(null);

  const { data: status, isLoading } = useQuery(
    ['mcp-key'],
    () => api.get('/mcp/key').then((r) => r.data)
  );

  const generateMutation = useMutation(
    () => api.post('/mcp/key').then((r) => r.data),
    {
      onSuccess: (data) => {
        setFreshKey(data.key);
        toast.success('Connection key generated');
        queryClient.invalidateQueries(['mcp-key']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to generate key'),
    }
  );

  const revokeMutation = useMutation(
    () => api.delete('/mcp/key').then((r) => r.data),
    {
      onSuccess: () => {
        setFreshKey(null);
        toast.success('Connection key revoked');
        queryClient.invalidateQueries(['mcp-key']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to revoke key'),
    }
  );

  // OAuth connections (Claude connectors) — one row per approved client.
  const { data: connectionsData } = useQuery(
    ['oauth-connections'],
    () => api.get('/oauth/connections').then((r) => r.data)
  );
  const connections = connectionsData?.connections || [];

  const disconnectMutation = useMutation(
    (id) => api.delete(`/oauth/connections/${id}`).then((r) => r.data),
    {
      onSuccess: () => {
        toast.success('Disconnected');
        queryClient.invalidateQueries(['oauth-connections']);
      },
      onError: (err) => toast.error(err.response?.data?.error || 'Failed to disconnect'),
    }
  );

  const copyToClipboard = (txt) => {
    navigator.clipboard?.writeText(txt);
    toast.success('Copied');
  };

  const keyForSnippets = freshKey || KEY_PLACEHOLDER;

  const claudeCodeOAuthSnippet = `claude mcp add --transport http gain-rhino-crm ${MCP_ENDPOINT}`;

  const claudeCodeSnippet =
    `claude mcp add --transport http crm ${MCP_ENDPOINT} --header "Authorization: Bearer ${keyForSnippets}"`;

  const claudeDesktopSnippet = JSON.stringify(
    {
      mcpServers: {
        crm: {
          command: 'npx',
          args: ['-y', 'mcp-remote', MCP_ENDPOINT, '--header', `Authorization: Bearer ${keyForSnippets}`],
        },
      },
    },
    null,
    2
  );

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
          <Bot size={28} /> Connect Claude
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Connect Claude (claude.ai, Claude Desktop or Claude Code) to your CRM. Claude can search
          leads, run reports, add notes, create tasks and send emails or SMS, always as you, with
          your permissions, and only within your organization's data.{' '}
          <Link to="/integrations/claude" className="text-primary-600 hover:underline">Full guide</Link>
        </p>
      </div>

      {/* OAuth connector (recommended) */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6 space-y-5">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Plug size={18} /> Connect with your CRM login (recommended)
        </h2>

        <div>
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
            <AppWindow size={16} /> claude.ai and Claude Desktop
          </h3>
          <ol className="text-sm text-gray-600 list-decimal pl-5 space-y-1 mb-2">
            <li>In Claude open <strong>Settings → Connectors</strong> and choose <strong>Add custom connector</strong> (or pick Gain Rhino CRM from the directory).</li>
            <li>Paste this URL and click <strong>Add</strong>, then <strong>Connect</strong>.</li>
            <li>Sign in to the CRM if asked and click <strong>Allow</strong>.</li>
          </ol>
          <CodeBlock text={MCP_ENDPOINT} onCopy={copyToClipboard} />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
            <Terminal size={16} /> Claude Code
          </h3>
          <p className="text-xs text-gray-500 mb-2">Run this, then type <code className="bg-gray-50 px-1 rounded">/mcp</code> in Claude Code and choose Authenticate:</p>
          <CodeBlock text={claudeCodeOAuthSnippet} onCopy={copyToClipboard} />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-800 mb-2">Connected apps</h3>
          {connections.length === 0 ? (
            <p className="text-sm text-gray-500">No apps connected yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
              {connections.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center justify-between gap-2 p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{c.clientName}</p>
                    <p className="text-xs text-gray-500">
                      Connected {new Date(c.connectedAt).toLocaleString()}
                      {' · '}
                      Last used {c.lastUsedAt ? new Date(c.lastUsedAt).toLocaleString() : 'never'}
                    </p>
                  </div>
                  <button
                    onClick={() => window.confirm(`Disconnect ${c.clientName}? It will lose access immediately.`) && disconnectMutation.mutate(c.id)}
                    disabled={disconnectMutation.isLoading}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
                  >
                    <Unplug size={14} /> Disconnect
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Connection key card */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <KeyRound size={18} /> Personal connection key (advanced)
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => {
                if (status?.hasKey && !window.confirm('Generate a new key? Your current key will stop working immediately.')) return;
                generateMutation.mutate();
              }}
              disabled={generateMutation.isLoading}
              className="inline-flex items-center gap-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
            >
              <RefreshCw size={14} className={generateMutation.isLoading ? 'animate-spin' : ''} />
              {status?.hasKey ? 'Regenerate' : 'Generate Key'}
            </button>
            {status?.hasKey && (
              <button
                onClick={() => window.confirm('Revoke this key? Claude will lose access until you generate a new one.') && revokeMutation.mutate()}
                disabled={revokeMutation.isLoading}
                className="inline-flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
              >
                <Trash2 size={14} /> Revoke
              </button>
            )}
          </div>
        </div>

        {isLoading ? (
          <p className="text-gray-400 text-sm">Loading...</p>
        ) : freshKey ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <p className="text-sm font-medium text-green-800 mb-2">
              Your new connection key — copy it now, it will not be shown again:
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 font-mono text-xs bg-white p-2 border border-green-200 rounded break-all">{freshKey}</code>
              <button onClick={() => copyToClipboard(freshKey)} className="p-2 text-green-700 hover:text-green-900">
                <Copy size={16} />
              </button>
            </div>
          </div>
        ) : status?.hasKey ? (
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              Active key: <code className="bg-gray-50 px-1.5 py-0.5 rounded text-xs">{status.keyPrefix}…</code>
            </p>
            <p className="text-xs text-gray-500">
              Created {status.createdAt ? new Date(status.createdAt).toLocaleString() : '-'}
              {' · '}
              Last used {status.lastUsedAt ? new Date(status.lastUsedAt).toLocaleString() : 'never'}
            </p>
            <p className="text-xs text-gray-500">
              The key is stored hashed and cannot be viewed again. Regenerate if you lost it.
            </p>
          </div>
        ) : (
          <p className="text-sm text-gray-500">
            No key yet. Generate one to connect Claude — it acts as your personal password for
            this connection, so keep it private.
          </p>
        )}
      </div>

      {/* Setup instructions */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 mb-6 space-y-6">
        <h2 className="text-lg font-semibold">Setup with a connection key</h2>
        <p className="text-xs text-gray-500 -mt-4">For tools that cannot sign in with OAuth. Prefer the login-based connection above.</p>

        <div>
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
            <Terminal size={16} /> Claude Code
          </h3>
          <p className="text-xs text-gray-500 mb-2">Run this once in your terminal:</p>
          <CodeBlock text={claudeCodeSnippet} onCopy={copyToClipboard} />
        </div>

        <div>
          <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2 mb-2">
            <AppWindow size={16} /> Claude Desktop
          </h3>
          <p className="text-xs text-gray-500 mb-2">
            Add this to your <code className="bg-gray-50 px-1 rounded">claude_desktop_config.json</code>{' '}
            (Settings → Developer → Edit Config), then restart Claude Desktop.
            Requires Node.js on your computer.
          </p>
          <CodeBlock text={claudeDesktopSnippet} onCopy={copyToClipboard} />
        </div>

        {!freshKey && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
            The snippets above contain a placeholder — generate a key first and they will
            include it automatically.
          </p>
        )}
      </div>

      {/* Security note */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold flex items-center gap-2 mb-2">
          <ShieldCheck size={18} /> How access works
        </h2>
        <ul className="text-sm text-gray-600 space-y-1.5 list-disc pl-5">
          <li>Claude connects as <strong>you</strong> — it can only see and do what your CRM account allows.</li>
          <li>All access stays within your organization; other companies' data is never reachable.</li>
          <li>Tools that change data or contact customers are marked as such, so Claude asks for your permission before running them unless you have allowed them permanently in Claude.</li>
          <li>Every request is recorded in the audit trail, like normal app usage.</li>
          <li>Disconnect an app or revoke the key at any time to cut off access instantly. Changing your password also signs every connection out.</li>
        </ul>
      </div>
    </div>
  );
};

export default ClaudeSettings;
